// packages imports
import { Box, Grid } from '@mui/material'
import { FC, useContext } from 'react'

// components imports
import { CourtsAutoComplete } from './CourtsAutoComplete'
import { CustomController } from '../../../common/CustomController'
import { LawTypes } from './LawTypes'

// others imports
import { StatusAutoComplete } from '../../../common/StatusAutoComplete'
import CheckboxController from '../../../common/CheckboxController'
import { useLocation } from 'react-router-dom'
import { useFetchRecentCaseQuery } from '../../../../../generated'
import { useFormContext } from 'react-hook-form'
import { GRAPHQL_QUERY_POLICY } from '../../../../constants'
import { AuthContext, CaseContext } from '../../../../context'
import OfficeAutoComplete from '../../../offices/OfficeAutocomplete'
import { CustomDatePickerController } from '../../../common/CustomDatePickerRController'
import UserDropdownWithForm from '../../../common/UserDropDownWithForm'
import { flexJustifySpaceBetween } from '../../../../theme/styleConstants'
import { hasAdminAccess } from '../../../../utils'

interface BasicDetailsProps {
  isEdit?: boolean
  isStatusClosed?: boolean
  selectedLawTypeId?: string
}

const BasicDetails: FC<BasicDetailsProps> = ({ isEdit, selectedLawTypeId, isStatusClosed = false }) => {
  const { pathname } = useLocation()
  const { setValue, watch, getValues } = useFormContext()
  const { currentUser } = useContext(AuthContext)
  const isConsultMatter = watch('isConsultMatter')
  const profitCenter = getValues('profitCenter')
  const location = getValues('location')
  const isConsultPath = pathname.includes('consult')
  const matterFieldName = isConsultPath ? 'consultMatterNumber' : 'caseNumber'

  const hasAdminPermission = hasAdminAccess(currentUser)

  const { currentCase } = useContext(CaseContext)
  const { isConsultMatter: isConsultCase, convertedToNormalMatter } = currentCase || {}

  const { data: recentCase } = useFetchRecentCaseQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: { isConsultMatter: isConsultPath }
  })

  function generateMatterNumber() {
    // Keyed off the edit flag, not the route id: the duplicate route (/matters/duplicate/:id) has an id too but
    // creates a new matter that needs a fresh number.
    if (isEdit) return

    const { fetchRecentCase } = recentCase || {}
    const { matterNo } = fetchRecentCase || {}

    // if (isConsultPath) setValue(matterFieldName, `C${matterNo}`)
    setValue(matterFieldName, `${matterNo}`)
    return `${matterNo}`
  }
  const matterNo = generateMatterNumber()
  const isConsult = isConsultPath || isConsultMatter

  return (
    <Box display="flex" flexDirection="column" gap={3.5}>
      <Box sx={[flexJustifySpaceBetween]}>
        {isConsult && <CheckboxController label={'Consult Matter'} name="isConsultMatter" disabled />}{' '}
        {hasAdminPermission && <CheckboxController label={'Internal Matter'} name="isInternalMatter" />}
      </Box>
      <CustomController controllerName="title" controllerLabel="Matter Title" fieldType="text" sx={{ my: 0 }} />
      <CustomController controllerName="docketNo" controllerLabel="Docket Number" fieldType="text" sx={{ my: 0 }} />
      <LawTypes isDisable={!isEdit} currentLawTypeId={selectedLawTypeId} />
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <OfficeAutoComplete
            name="profitCenter"
            label="Profit Center"
            currentOffice={profitCenter?.id ? profitCenter : undefined}
            customOnChange={option => setValue('profitCenter', { id: option?.value, name: option?.label })}
            autoSelectOnTab
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <OfficeAutoComplete
            name="location"
            label="Location"
            isProfitCenter={false}
            currentOffice={location?.id ? location : undefined}
            customOnChange={option => setValue('location', { id: option?.value, name: option?.label })}
            autoSelectOnTab
          />
        </Grid>
      </Grid>
      <CourtsAutoComplete isEdit={isEdit} />
      <CustomController
        fieldType="text"
        defaultValue={matterNo}
        controllerName={isConsult ? 'consultMatterNumber' : 'caseNumber'}
        controllerLabel={isConsult ? 'Consult Matter Number' : 'Matter Number'}
        sx={{
          my: 0
        }}
        autoFocus
        isDisabled
      />
      {!isConsultCase && convertedToNormalMatter && (
        <CustomController
          fieldType="text"
          controllerName={'consultMatterNumber'}
          controllerLabel={'Consult Matter Number'}
          sx={{
            my: 0
          }}
          autoFocus
          isDisabled
        />
      )}
      {isEdit && <StatusAutoComplete isClosed={isStatusClosed} />}
      <CustomController
        isMultiLine
        rowsLength={5}
        fieldType="text"
        controllerName="description"
        controllerLabel="Insurance/Miscellaneous"
        sx={{ my: 0 }}
      />
      {(isConsultPath || isConsultMatter) && (
        <Grid item xs={12}>
          <UserDropdownWithForm name="openedBy" label="Opened By" />
        </Grid>
      )}
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <CustomDatePickerController controllerName="openDate" controllerLabel="Matter Opened" sx={{ my: 0 }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomDatePickerController controllerName="completedDate" controllerLabel="Work Completed" sx={{ my: 0 }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomDatePickerController controllerName="closeDate" controllerLabel="Matter Closed" sx={{ my: 0 }} />
        </Grid>
      </Grid>
    </Box>
  )
}

export default BasicDetails
